import { getHighestCountryRiskStatus } from '@app/src/pages/TopicalRisk/Drawer/topicalRiskDrawerUtils'
import { RiskIndexType, RiskStatus } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopicRisk } from '@app/src/types/topicalRisk'
import { RISK_STATUS_DESC_ORDER } from '@app/src/utils/riskStatusHelpers'

const contributesRiskLevel = (status?: RiskStatus): boolean =>
  status !== undefined && status !== RiskStatus.Deactivated && status !== RiskStatus.NotApplicable

/**
 * The risk indices that actually produced a level for this topic. An index the organization switched
 * off contributes nothing, and neither does one switched on that found no data for this provider.
 */
export function getContributingRiskIndexTypes(risk: SustainabilityTopicRisk): RiskIndexType[] {
  const countryRiskStatus = getHighestCountryRiskStatus(risk.headquarterRiskStatus, risk.operationRiskStatus)

  return [
    ...(contributesRiskLevel(countryRiskStatus) ? [RiskIndexType.Country] : []),
    ...(contributesRiskLevel(risk.industryRiskStatus) ? [RiskIndexType.Industry] : []),
  ]
}

/**
 * The combined level as the backend calculated it. Risks calculated before the column existed
 * carry no value, so fall back to the previous highest-wins rule rather than showing nothing.
 */
export function getInherentRiskStatus(risk: SustainabilityTopicRisk): RiskStatus | undefined {
  if (risk.inherentRiskStatus) return risk.inherentRiskStatus

  const countryRiskStatus = getHighestCountryRiskStatus(risk.headquarterRiskStatus, risk.operationRiskStatus)
  const statuses = [countryRiskStatus, risk.industryRiskStatus].filter(Boolean)
  if (!statuses.length) return undefined

  const priorityOrder = [...RISK_STATUS_DESC_ORDER, RiskStatus.Deactivated]
  return priorityOrder.find(status => statuses.includes(status))
}

/**
 * An activated industry index still produces no level when the supplier has no sub-industry, since
 * risk values only exist for NACE divisions. GetIndustryRisk reports that and a genuinely empty
 * index identically as ANotApplicable, so Providers.SubIndustryId is what tells them apart.
 * A hasSubIndustry of undefined means we have not established it yet.
 */
export function isSubIndustryMissing(riskStatus?: RiskStatus, hasSubIndustry?: boolean): boolean {
  return riskStatus === RiskStatus.NotApplicable && hasSubIndustry === false
}

/**
 * An index the organization has switched off still earns a row, so the reader can see it is
 * available. One that is switched on but holds no data for this provider tells them nothing,
 * unless we can explain why — a supplier with no sub-industry gets a row saying so.
 */
export function shouldShowRiskIndexRow(riskStatus?: RiskStatus, isSubIndustryMissingForRow = false): boolean {
  return isSubIndustryMissingForRow || (riskStatus !== undefined && riskStatus !== RiskStatus.NotApplicable)
}

export function isRiskIndexDeactivated(riskStatus?: RiskStatus): boolean {
  return riskStatus === undefined || riskStatus === RiskStatus.Deactivated
}

/**
 * Which description the Inherent risk card shows, based on how many indices contribute.
 */
export function getInherentRiskDescriptionKey(contributingTypes: RiskIndexType[]): string {
  if (contributingTypes.length > 1) return 'topicalRisk.drawer.inherentRiskDescription'
  if (contributingTypes[0] === RiskIndexType.Industry) return 'topicalRisk.drawer.inherentRiskDescriptionIndustryOnly'
  if (contributingTypes[0] === RiskIndexType.Country) return 'topicalRisk.drawer.inherentRiskDescriptionCountryOnly'
  return 'topicalRisk.drawer.inherentRiskDescriptionNone'
}
